/* eslint-disable sonarjs/cognitive-complexity */
import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import { addHours, addYears, startOfTomorrow, subDays, subHours, subYears } from 'date-fns';
import { Matcher } from 'react-day-picker';
import Auth from 'modules/Auth';
import { Store } from 'components/common/types/Store.types';
import { RootState } from 'store';
import { getIsReseller } from 'store/publisher/selectors';
import { SOURCE_SYSTEM } from 'consts/sourceSystem';
import { PermissionsEnum } from 'consts/permissions';
import { DATE_TIME_PICKER_YEARS_AFTER, DATE_TIME_PICKER_YEARS_BEFORE } from 'consts/dateTimePicker';
import { formatIndexOptimisationWithEmptyDisplayName } from 'utils/formatIndexOptimisation';
import { CampaignType } from 'components/pages/Planner/PlannerSections/types';
import { VisibleAsset } from 'components/common/types/Planner.types';
import { FeatureFlags } from 'components/common/types/Features.types';
import { DirectSalesNavigationLineData } from 'components/common/types/DirectSalesHeader';
import { DealStatus, DealType, FrontEndType, Line } from 'components/common/types/Deal.types';
import {
  DirectSalesLine,
  DirectSalesLockStatusOption,
  DirectSalesState,
  DirectSalesStatusOption,
} from 'components/common/types/DirectSalesCampaign.types';
import { LIVE_CAMPAIGN_STATUSES } from 'consts/campaignStatus';
import { getOffsetDifference } from 'utils/getOffsetDifference';
import { validateNameLength } from 'utils/validateNameLength';

import { isDirectSalesLinePriced } from 'components/pages/Planner/hooks/utils/getIsDirectSalesLinePriced';
import { getActionConditionsValidator } from 'components/pages/DealWithLines/common/getActionConditions';
import { ceilHour, getDirectSalesMandatoryCampaignFields } from './utils';
import { Loading, LocalLoading } from './types';
import { getIsProgrammaticDraftCampaignTransitionEnabled } from './selectors/getIsProgrammaticDraftCampaignTransitionEnabled';
import { getIsProgrammaticEndedAddNewLineEnabled } from './selectors/getIsProgrammaticEndedAddNewLineEnabled';
import { getHasDealReadOnlyPermission } from './selectors/getHasDealReadOnlyPermission';
import { getDirectSalesMediaType } from './selectors/getDirectSalesMediaType';
import { getCurrentLineId } from './selectors/getCurrentLineId';
import { getCampaignStatus } from './selectors/getCampaignStatus';
import { getDealType } from './selectors/getDealType';
import { getIsNewDeal } from './selectors/getIsNewDeal';
import {
  getSummaryAllocatedFrames,
  getSummaryAllocatedImpressions,
  getSummaryTotalCost,
} from './selectors/getSummaryAvailability';

export const getFrameIdIndexesOnViewport = createSelector(
  (state: Store) => state.dealManagement.plannerSettings.frameIdIndexes,
  (state: Store) => state.dealManagement.plannerSettings.assetsOnViewport,
  (frameIdIndexes = [], assetsOnViewport = []) =>
    frameIdIndexes.length
      ? frameIdIndexes.filter((frameIdIndex) =>
          assetsOnViewport.some((asset: VisibleAsset) => asset.isVisible && asset.frameId === frameIdIndex.frameId),
        )
      : [],
);

export const getIsValidSimplifiedPlanningAvailabilityRequest = createSelector(
  (state: RootState) => state.publisher.publisherFeatures,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.frontEndType,
  (state: RootState) => state.dealManagement.plannerSettings.isSimplifiedPlanningMode,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.startDate,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.endDate,
  (publisherFeatures, frontEndType, isSimplifiedPlanningMode, startDate, endDate) => {
    const hasSimplifiedPlanningEnabled = !!publisherFeatures[FeatureFlags.SIMPLIFIED_PLANNING];

    if (
      hasSimplifiedPlanningEnabled &&
      frontEndType === FrontEndType.PLANNER &&
      isSimplifiedPlanningMode &&
      !!startDate &&
      !!endDate
    )
      return true;

    return false;
  },
);

export const getIsValidAvailabilityRequest = createSelector(
  getIsReseller,
  (state: RootState) => state.dealManagement.isCpmCampaignLevel,
  (state: RootState) => state.dealManagement.commonDeal.advertiser,
  (state: RootState) => state.dealManagement.commonDeal.brand,
  (state: RootState) => state.dealManagement.commonDeal.cpm,
  (state: RootState) => state.dealManagement.commonDeal.productCategory,
  (state: RootState) => state.dealManagement.commonDeal.sourceSystem,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.cpm,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.maxCPM,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.environments,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.startDate,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.endDate,
  (state: RootState) => state.dealManagement.programmatic.dsp,
  (state: RootState) => state.environment.environmentId,
  (state: RootState) => state.publisher.publisherFeatures,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.mediaOwnerMeta,
  getIsValidSimplifiedPlanningAvailabilityRequest,
  (state: RootState) => state.reseller.isInternalPublisher,
  getDealType,
  getIsProgrammaticEndedAddNewLineEnabled,
  (state: RootState) => state.dealManagement.commonDeal.dealName,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.name,
  (
    isReseller,
    isCpmCampaignLevel,
    advertiser,
    brand,
    dealCpm,
    productCategory,
    sourceSystem,
    lineCpm,
    maxCPM,
    environments,
    startDate,
    endDate,
    dsp,
    environmentId,
    publisherFeatures,
    mediaOwnerMeta,
    isSimplifiedPlanningAvailabilityRequest,
    hasVIOOHSelectFeatures,
    dealType,
    isProgrammaticEndedAddNewLineEnabled,
    dealName,
    currentLineName,
  ) => {
    const { isValid: isValidDealName } = validateNameLength({ name: dealName });
    const { isValid: isValidCurrentLineName } = validateNameLength({ name: currentLineName });
    const hasAdsDealLevelCPMEnabled = publisherFeatures[FeatureFlags.DEAL_LEVEL_CPM]?.includes(environmentId);
    const cpm = hasAdsDealLevelCPMEnabled && isCpmCampaignLevel ? dealCpm : lineCpm;
    const isCorrectCpmValue = cpm;
    const isDV360Deal = sourceSystem === SOURCE_SYSTEM.DV360;
    const hasRequiredMaxCPM = isReseller && hasVIOOHSelectFeatures ? !!maxCPM && maxCPM <= cpm : true;
    const hasRequiredEnvironments = isReseller || environments?.length > 0;
    const hasRequiredMediaOwners = isReseller ? mediaOwnerMeta?.length > 0 : true;
    const hasRequiredSchedule = !!startDate && !!endDate;
    const isDealTypeGuaranteed = dealType === DealType.GUARANTEED;

    if (!isValidDealName || !isValidCurrentLineName) return false;

    if (isSimplifiedPlanningAvailabilityRequest) return true;

    const hasRequiredGuaranteedFields =
      !isDealTypeGuaranteed || (!!advertiser?.code && !!brand?.code && !!productCategory?.code);

    if (!hasRequiredEnvironments || !hasRequiredMediaOwners || !hasRequiredSchedule || !dsp?.code) {
      return false;
    }

    if (isProgrammaticEndedAddNewLineEnabled) return true;

    if (!isDV360Deal && (!hasRequiredGuaranteedFields || !isCorrectCpmValue || !hasRequiredMaxCPM)) {
      return false;
    }

    return true;
  },
);

export const getIsValidDirectSalesAvailabilityRequest = createSelector(
  (state: Store) => state.dealManagement.commonDeal.advertiser,
  (state: Store) => state.dealManagement.commonDeal.brand,
  (state: Store) => state.dealManagement.commonDeal.productCategory,
  (state: Store) => state.dealManagement.commonDeal.currentLine.environments,
  (state: Store) => state.dealManagement.commonDeal.currentLine.startDate,
  (state: Store) => state.dealManagement.commonDeal.currentLine.endDate,
  getDirectSalesMediaType,
  (advertiser, brand, productCategory, environments, startDate, endDate, mediaType) => {
    return !!(
      advertiser?.code &&
      brand?.code &&
      productCategory?.code &&
      environments.length !== 0 &&
      startDate &&
      endDate &&
      mediaType
    );
  },
);

export const getIsNewAvailabilityFetched = createSelector(
  (state: RootState) => state.dealManagement.isForecastedAllocation,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.availability.allocatedFrames,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.availability.allocatedImpressions,
  (state: RootState) => state.dealManagement.commonDeal.currentLine.availability.totalCost,
  (isForecastedAllocation, allocatedFrames, allocatedImpressions, totalCost) => {
    return !isForecastedAllocation && !allocatedFrames && !allocatedImpressions && !!totalCost;
  },
);

export const getDealAllocationData = createSelector(
  (state: Store) => state.dealManagement.isForecastedAllocation,
  getSummaryAllocatedFrames,
  getSummaryAllocatedImpressions,
  getSummaryTotalCost,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.summary?.availability.allocatedFrames,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.summary?.availability.allocatedImpressions,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.summary?.availability.totalCost,
  (
    isForecastedAllocation,
    allocatedFrames,
    allocatedImpressions,
    totalCost,
    preservedAllocatedFrames,
    preservedAllocatedImpressions,
    preservedTotalCost,
  ) => {
    if (isForecastedAllocation) {
      return {
        allocatedFrames: preservedAllocatedFrames,
        allocatedImpressions: preservedAllocatedImpressions,
        totalCost: preservedTotalCost,
      };
    }

    return {
      allocatedFrames,
      allocatedImpressions,
      totalCost,
    };
  },
);

export const getBackupCurrentLineAllocation = createSelector(
  (state: Store) => state.dealManagement.commonDeal.currentLine.id,
  (state: Store) => state.dealManagement.backupFormData.lines,
  (currentLineId = '', backupFormDataLines = []) => {
    const backupAvailability = backupFormDataLines.find((line) => line.id === currentLineId)?.availability;

    return {
      allocatedFrames: backupAvailability?.allocatedFrames || 0,
      allocatedImpressions: backupAvailability?.allocatedImpressions || 0,
      assets: backupAvailability?.assets || [],
      totalCost: backupAvailability?.totalCost || 0,
    };
  },
);

export const getLowerCaseLineNames = createSelector(
  (state: Store) => state.dealManagement.backupFormData.lines,
  (lines = []) => lines.map((line) => line.name?.toLowerCase()),
);

export const getIsEveryLineWithId = createSelector(
  (state: Store) => state.dealManagement.backupFormData.lines,
  (lines = []) => lines.every((line) => !!line.lineId),
);

export const getEveryLineState = createSelector(
  (state: Store) => state.dealManagement.backupFormData?.lines,
  (lines = []) => {
    if (!lines) return [];

    return lines.filter((line: Line | DirectSalesLine) => 'state' in line).map((line: DirectSalesLine) => line.state);
  },
);

export const getEveryLineStatus = createSelector(
  (state: Store) => state.dealManagement.backupFormData?.lines,
  (lines = []) =>
    lines.filter((line: Line | DirectSalesLine) => 'state' in line).map((line: DirectSalesLine) => line.state.status),
);

export const getMapRouteFrameCodesLength = createSelector(
  (state: Store) => state.dealManagement.commonDeal.currentLine.mapRouteFrameCodes,
  (mapRouteFrameCodes = []) => mapRouteFrameCodes.length,
);

export const getEveryLineStateGroupedByLineId = createSelector(
  (state: Store) => state.dealManagement.backupFormData.lines,
  (lines = []) =>
    lines.reduce((acc: Record<string, DirectSalesState>, curr: DirectSalesLine) => {
      acc[curr.lineId] = curr.state;
      return acc;
    }, {}),
);

export const getCampaignOptionDropDateDisabledDays = createSelector(
  (state: Store) => state.dealManagement.backupFormData.lines,
  (lines = []): Matcher[] => {
    const tomorrow = startOfTomorrow();
    const [firstStartDate, ...restStartDates] = lines.map((line: DirectSalesLine) => new Date(line.startDate ?? ''));

    const minDate = restStartDates.reduce((acc, curr) => {
      if (curr.getTime() < acc.getTime()) return curr;
      return acc;
    }, firstStartDate);

    const maxCampaignDropDate = subDays(minDate, 1);

    if (maxCampaignDropDate.getTime() < tomorrow.getTime()) {
      return [
        {
          from: subYears(new Date(), DATE_TIME_PICKER_YEARS_BEFORE + 1),
          to: addYears(new Date(), DATE_TIME_PICKER_YEARS_AFTER + 1),
        },
      ];
    }

    return [{ before: tomorrow }, { after: maxCampaignDropDate }];
  },
);

export const getLineOptionDropDateDisabledDays = createSelector(
  (state: Store) => state.dealManagement.commonDeal.currentLine.startDate,
  (startDate: string | Date | null): Matcher[] => {
    const today = new Date();
    const tomorrow = startOfTomorrow();

    const allDaysDisabled: Matcher[] = [
      {
        from: subYears(today, DATE_TIME_PICKER_YEARS_BEFORE + 1),
        to: addYears(today, DATE_TIME_PICKER_YEARS_AFTER + 1),
      },
    ];

    if (!startDate) return allDaysDisabled;

    const maxLineDropDate = subDays(new Date(startDate), 1);

    if (maxLineDropDate.getTime() < tomorrow.getTime()) return allDaysDisabled;

    return [{ before: tomorrow }, { after: maxLineDropDate }];
  },
);

export const getLiveLineSplitDays = createSelector(
  [
    (state: Store) => state.dealManagement.commonDeal.currentLine.endDate,
    (state: Store) => state.publisher.startDateOffset,
    (state: Store) => state.publisher.configuration.timezone,
  ],
  (
    endDate: Date,
    startDateOffset: number,
    timezone: string,
  ): { endDate: Date; minLineSplitDate: Date; maxLineSplitDate: Date } => {
    const endDateWithTime = new Date(endDate).setHours(23, 59, 59);
    const minDateOffset = startDateOffset + getOffsetDifference(timezone);

    const minLineSplitDate = ceilHour(addHours(new Date(), minDateOffset));
    const maxLineSplitDate = subHours(endDateWithTime, 1);

    return {
      endDate: new Date(endDateWithTime),
      minLineSplitDate,
      maxLineSplitDate,
    };
  },
);

export const getIsAdvertiserInfoChanged = createSelector(
  (state: Store) => state.dealManagement.backupFormData.commonDeal.advertiser?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.brand?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.productCategory?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.agency?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.specialist?.code,
  (state: Store) => state.dealManagement.commonDeal.advertiser?.code,
  (state: Store) => state.dealManagement.commonDeal.brand?.code,
  (state: Store) => state.dealManagement.commonDeal.productCategory?.code,
  (state: Store) => state.dealManagement.commonDeal.agency?.code,
  (state: Store) => state.dealManagement.commonDeal.specialist?.code,
  (
    backupAdvertiserCode,
    backupBrandCode,
    backupProductCategoryCode,
    backupAgencyCode,
    backupSpecialistCode,
    advertiserCode,
    brandCode,
    productCategoryCode,
    agencyCode,
    specialistCode,
  ) => {
    const checkIfChanged = <T>(
      backupValueCode: T | undefined,
      currentValueCode: T | undefined,
      isRequired: boolean,
    ): boolean => {
      if (isRequired) {
        return !!backupValueCode && !!currentValueCode && backupValueCode !== currentValueCode;
      }
      return backupValueCode !== currentValueCode;
    };

    const isAdvertiserChanged = checkIfChanged(backupAdvertiserCode, advertiserCode, true);
    const isBrandChanged = checkIfChanged(backupBrandCode, brandCode, true);
    const isProductCategoryChanged = checkIfChanged(backupProductCategoryCode, productCategoryCode, true);
    const isAgencyChanged = checkIfChanged(backupAgencyCode, agencyCode, false);
    const isSpecialistChanged = checkIfChanged(backupSpecialistCode, specialistCode, false);

    return {
      isAdvertiserChanged,
      isBrandChanged,
      isProductCategoryChanged,
      isAgencyChanged,
      isSpecialistChanged,
      isAnyChanged:
        isAdvertiserChanged || isBrandChanged || isProductCategoryChanged || isAgencyChanged || isSpecialistChanged,
    };
  },
);

export const getIsAnyAdvertiserInfoChanged = createSelector(
  getIsAdvertiserInfoChanged,
  ({ isAnyChanged }) => isAnyChanged,
);

export const getModalVisibilityConditions = createSelector(
  getIsAnyAdvertiserInfoChanged,
  (state: Store) => state.dealManagement.commonDeal.bookingStatusCode,
  (isAnyChanged, bookingStatusCode) => {
    const shouldShowAdvertiserInfoModal =
      [DealStatus.APPROVED, DealStatus.PENDING_APPROVAL, DealStatus.REJECTED, DealStatus.LIVE].includes(
        bookingStatusCode,
      ) && isAnyChanged;

    return {
      shouldShowAdvertiserInfoModal,
    };
  },
);

export const getIsNewLine = createSelector(
  (state: Store) => state.dealManagement.commonDeal.currentLine?.id,
  (state: Store) => state.dealManagement.commonDeal.currentLine?.lineId,
  (id, lineId) => {
    return !id || !lineId;
  },
);

export const getIsDirectSalesSessionReadOnly = createSelector(
  (state: Store) => state.dealManagement.directSalesSessionLock,
  (directSalesSessionLock) => {
    const { userName } = Auth.getData() || {};
    const isSessionLocked = Boolean(directSalesSessionLock?.expires && directSalesSessionLock?.owner);
    return isSessionLocked && userName !== directSalesSessionLock.owner;
  },
);

export const getIsSessionLockedByCurrentUser = createSelector(
  (state: Store) => state.dealManagement.directSalesSessionLock,
  (directSalesSessionLock) => {
    const { userName } = Auth.getData() || {};
    const isSessionLocked = Boolean(directSalesSessionLock?.expires && directSalesSessionLock?.owner);
    return isSessionLocked && userName === directSalesSessionLock.owner;
  },
);

export const getDirectSalesDisplayedLinesList = createSelector(
  (state: Store) => state.dealManagement.backupFormData.lines,
  (lines: DirectSalesLine[]): DirectSalesNavigationLineData[] => {
    return lines.map(({ lineId, id, name, lockState }) => ({ lineId, id, name, lockState }));
  },
);

export const getAllocation = createSelector(
  [(state: Store) => state.dealManagement.directSales.allocation, (_, id: number | null) => id],
  (allocation, id) => {
    if (!id || !allocation[id])
      return {
        allocated: undefined,
        status: undefined,
      };

    return {
      allocated: allocation[id].allocated,
      status: allocation[id].status,
    };
  },
);

export const getSessionExpirationDate = createSelector(
  (state: Store) => state.dealManagement.directSalesSessionLock?.expires,
  (expiresDate: Date | string) => (expiresDate ? new Date(expiresDate) : null),
);

export const getBackupLine = createSelector(
  (state: Store) => state.dealManagement.backupFormData.lines,
  getCurrentLineId,
  (lines, currentLineId) => lines.find((line) => line.lineId === currentLineId),
);

export const getBackupLineNameByLineId = createSelector(
  [(state: Store) => state.dealManagement.backupFormData.lines, (_, id: string) => id],
  (lines, id) => {
    return lines.find((line) => line.lineId === id)?.name;
  },
);

export const getIsSingleLine = createSelector(
  (state: Store) => state.dealManagement.backupFormData.lines,
  (lines: DirectSalesLine[] | Line[]) => lines.length === 1,
);

export const getIsAnyLineFetchingAvailability = createSelector(
  (state: Store) => state.dealManagement.commonDeal.currentLine.isFetchingAvailability,
  (state: Store) => state.dealManagement.backupFormData.lines,
  (currentLineIsFetchingAvailability, lines) => {
    return currentLineIsFetchingAvailability || lines.some((line) => line.isFetchingAvailability);
  },
);

export const getIsFetchingAdvancedAvailabilityReport = createSelector(
  (state: Store) => state.dealManagement.isFetchingAdvancedAvailabilityReport,
  (isFetchingAdvancedAvailabilityReport) => isFetchingAdvancedAvailabilityReport,
);

export const getIsLineBeingSaved = createSelector(
  [
    (state: Store) => state.dealManagement.commonDeal.currentLine?.lineId,
    (state: Store) => state.dealManagement.commonDeal.currentLine?.isSaving,
    (state: Store) => state.dealManagement.backupFormData?.lines ?? [],
    (_, lineId?: string) => lineId,
  ],
  (currentLineId, currentLineIsSaving, backupLines, lineId) => {
    if (backupLines?.some((line) => line.isSaving)) return true;

    if (!lineId || lineId === currentLineId) return currentLineIsSaving;

    const backupLine = backupLines.find((line) => line.lineId === lineId);

    return backupLine?.isSaving || false;
  },
);

export const getIsLineCancelled = createSelector(
  [
    (state: Store) => state.dealManagement.commonDeal.currentLine?.lineId,
    (state: Store) => state.dealManagement.directSales?.state?.status,
    (state: Store) => (state.dealManagement.backupFormData?.lines ?? []) as DirectSalesLine[],
    (state: Store) => state.dealManagement.directSales?.transientLineStates,
    (_, lineId?: string) => lineId,
  ],
  (currentLineId, currentLineStatus, backupLines, transientLineStates, lineId) => {
    const effectiveLineId = lineId || currentLineId;
    const transientStatus = transientLineStates?.[effectiveLineId]?.status;

    if (transientStatus) return transientStatus === DirectSalesStatusOption.CANCELLED;

    if (!lineId || lineId === currentLineId) {
      return currentLineStatus === DirectSalesStatusOption.CANCELLED;
    }

    const backupLine = backupLines.find((line) => line.lineId === lineId);

    return backupLine?.state?.status === DirectSalesStatusOption.CANCELLED;
  },
);

export const isAnyLineStatusIncluded = createSelector(
  [
    (state: Store) => state.dealManagement.backupFormData.lines as DirectSalesLine[],
    (state: Store) => state.dealManagement.campaignType,
    (_: Store, statusList: DirectSalesStatusOption[]) => statusList,
  ],
  (lines, campaignType, statusList): boolean =>
    campaignType === CampaignType.DIRECT && lines.some(({ state }) => statusList.includes(state.status)),
);

export const isLineStatusIncluded = createSelector(
  [
    (state: Store) => state.dealManagement.backupFormData.lines,
    (state: Store) => state.dealManagement.campaignType,
    (_, args: { id: string; statusList: DirectSalesStatusOption[] }) => args,
  ],
  (lines, campaignType, args) => {
    if (campaignType !== CampaignType.DIRECT) return false;

    const found = lines.find((line) => line.lineId === args.id) as DirectSalesLine | undefined;

    if (found?.state.status) {
      return args.statusList.includes(found.state.status);
    }

    return false;
  },
);

export const isEveryDirectSalesLinePriced = createSelector(
  (state: Store) => state.dealManagement.backupFormData.lines as DirectSalesLine[],
  (lines): boolean =>
    lines.every(
      ({ state }) => state.status === DirectSalesStatusOption.CANCELLED || isDirectSalesLinePriced(state.status),
    ),
);

export const getIsCurrentLineLiveOrEnded = createSelector(
  (state: Store) => state.dealManagement.directSales.state.status,
  (status): boolean => LIVE_CAMPAIGN_STATUSES.includes(status),
);

export const getIsCampaignEnded = createSelector(
  [(state: Store) => state.dealManagement.commonDeal.bookingStatusCode],
  (bookingStatusCode) =>
    [DealStatus.REJECTED, DealStatus.ENDED, DealStatus.TERMINATED, DealStatus.CANCELLED].includes(bookingStatusCode),
);

export const getCampaignAllowedTransitions = createSelector(
  [
    (state: Store) => state.dealManagement.directSales.transientLineStates,
    (state: Store) => state.dealManagement.meta.originalLineStatuses,
    (state: Store) => state.dealManagement.meta.allowedStateTransitions,
    (_, statusOption?: DirectSalesStatusOption) => statusOption,
  ],
  (
    transientLineStates = {},
    originalLineStatuses = {},
    allowedStateTransitions = {},
    statusOption = DirectSalesStatusOption.DRAFT_UPDATING,
  ) => {
    let transitions = [];

    if (statusOption === DirectSalesStatusOption.LIVE) {
      transitions = allowedStateTransitions[DirectSalesStatusOption.CONFIRMED] || [];
    } else if (statusOption === DirectSalesStatusOption.ENDED) {
      transitions = allowedStateTransitions[DirectSalesStatusOption.CANCELLED] || [];
    } else {
      transitions = allowedStateTransitions[statusOption] || [];
    }

    const originalLineStatusesLength = Object.keys(originalLineStatuses).length;
    const transientLineStatesLength = Object.keys(transientLineStates).length;

    if (originalLineStatusesLength > 0 && originalLineStatusesLength < transientLineStatesLength) {
      return transitions.filter((transition) => transition !== DirectSalesStatusOption.CANCELLED);
    }

    return transitions;
  },
);

export const getLineAllowedTransitions = createSelector(
  [
    (state: Store) => state.dealManagement.directSales.state?.status,
    (state: Store) => state.dealManagement.meta.allowedStateTransitions,
    (_, lineTransientStatusOption?: DirectSalesStatusOption) => lineTransientStatusOption,
  ],
  (lineStatus, allowedStateTransitions = {}, lineTransientStatusOption = undefined) => {
    if (lineStatus === DirectSalesStatusOption.LIVE)
      return allowedStateTransitions[DirectSalesStatusOption.CONFIRMED] || [];

    if (lineStatus === DirectSalesStatusOption.ENDED)
      return allowedStateTransitions[DirectSalesStatusOption.CANCELLED] || [];

    return allowedStateTransitions[lineTransientStatusOption ?? lineStatus] || [];
  },
);

export const isCampaignStatusIncluded = createSelector(
  [getCampaignStatus, (_, statusList: DirectSalesStatusOption[]) => statusList],
  (campaignStatus, statusList) => statusList.includes(campaignStatus),
);

export const isCampaignStatusPriced = createSelector(getCampaignStatus, (campaignStatus) =>
  [
    DirectSalesStatusOption.PROPOSAL,
    DirectSalesStatusOption.OPTION,
    DirectSalesStatusOption.CONFIRMED,
    DirectSalesStatusOption.LIVE,
  ].includes(campaignStatus),
);

export const getIsLineLiveOrEnded = createSelector(
  [(state: Store) => state, (_, args: { id: string; statusList: DirectSalesStatusOption[] }) => args],
  (state, args) => isLineStatusIncluded(state, { id: args.id, statusList: LIVE_CAMPAIGN_STATUSES }),
);

export const getIsLineLocked = createSelector(
  [
    (state: Store) => state.dealManagement.commonDeal.currentLine?.lineId,
    (state: Store) => state.dealManagement.directSales?.lockState?.status,
    (state: Store) => (state.dealManagement.backupFormData?.lines ?? []) as DirectSalesLine[],
    (_, lineId?: string) => lineId,
  ],
  (currentLineId, currentLineLockedStatus, backupLines, lineId) => {
    if (!lineId || lineId === currentLineId) return currentLineLockedStatus === DirectSalesLockStatusOption.LOCKED;

    const backupLine = backupLines.find((line) => line.lineId === lineId);

    return backupLine?.lockState?.status === DirectSalesLockStatusOption.LOCKED;
  },
);

export const getAssetsLength = createSelector(
  (state: Store) => state.dealManagement.commonDeal.currentLine?.availability?.assets?.length,
  (length) => length,
);

export const getProgrammaticShowSaveButton = createSelector(
  [
    (state: Store) => state.dealManagement.commonDeal.bookingStatusCode,
    (state: Store) => state.dealManagement.commonDeal.currentLine.frontEndType,
    (state: Store) => state.dealManagement.isCampaignDrawerOpen,
  ],
  (bookingStatusCode, frontEndType, isCampaignDrawerOpen) =>
    !!bookingStatusCode && frontEndType !== FrontEndType.STANDARD && isCampaignDrawerOpen,
);

export const getIsProgrammaticLineLevelChange = createSelector(
  [
    (state: Store) => state.dealManagement.backupFormData?.lines,
    (state: Store) => state.dealManagement.commonDeal,
    (state: Store) => state.dealManagement.commonDeal.currentLine,
    getIsNewDeal,
    (state: Store) => state.dealManagement.isForecastedAllocation,
    (state: Store) => state.dealManagement.isCpmCampaignLevel,
    (state: Store) => state.nonMediaServices.areServicesChanged,
  ],
  (backupLines, commonDeal, currentLine, isNewDeal, isForecastedAllocation, isCpmCampaignLevel, areServicesChanged) => {
    if (isNewDeal && !commonDeal.bookingStatusCode) return false;

    const currentBackupLine = backupLines.find((line) => line.id === commonDeal.currentLine.id) ?? backupLines[0] ?? {};

    const {
      availability: backupAvailability,
      name: backupName,
      startDate: backupStartDate,
      endDate: backupEndDate,
      cpm: backupCpm,
      environments: backupEnvironments,
      impressions: backupImpressions,
      budget: backupBudget,
      frames: backupFrames,
      selectedDays: backupSelectedDays,
      sot: backupSot,
      sweep: backupSweep,
      floorSot: backupFloorSot,
      ceilingSot: backupCeilingSot,
      venueTaxonomies: backupVenueTaxonomies,
      indexOptimisation: backupIndexOptimisation,
      proximity: backupProximity,
      segment: backupSegment,
      productFormats: backupProductFormats,
      routeFrameCodes: backupRouteFrameCodes,
      listFiles: backupListFiles,
      countries: backupCountries,
      streets: backupStreets,
      cities: backupCities,
      counties: backupCounties,
      postCodes: backupPostCodes,
      tags: backupTags,
      distributionOfAvailabilityFilter: backupDistributionOfAvailabilityFilter,
      visualUnitCodes: backupVisualUnitCodes,
      visualUnitFiles: backupVisualUnitFiles,
      mediaOwnerMeta: backupMediaOwnerMeta,
      minCPM: backupMinCPM,
      maxCPM: backupMaxCPM,
      frameCpmFiles: backupFrameCpmFiles,
      multiTargetingFile: backupMultiTargetingFile,
    } = currentBackupLine;

    const {
      availability,
      name,
      startDate,
      endDate,
      cpm,
      environments,
      impressions,
      budget,
      frames,
      selectedDays,
      sot,
      sweep,
      floorSot,
      ceilingSot,
      venueTaxonomies,
      indexOptimisation,
      proximity: { openStreetMapPoi, plannerPoi },
      segment: { selectedSegments },
      productFormats,
      routeFrameCodes,
      listFiles,
      countries,
      streets,
      cities,
      counties,
      postCodes,
      tags,
      distributionOfAvailabilityFilter,
      visualUnitCodes,
      visualUnitFiles,
      mediaOwnerMeta,
      minCPM,
      maxCPM,
      frameCpmFiles,
      bookingStatusCode,
      multiTargetingFile,
    } = currentLine;

    const hasDistributionSelected = !!distributionOfAvailabilityFilter.selectedItems.length;
    const shouldCompareAvailability = isForecastedAllocation && !hasDistributionSelected;
    const isLineLevelCpm = !isCpmCampaignLevel;

    const areServicesOmitted =
      bookingStatusCode === DealStatus.CANCELLED ||
      bookingStatusCode === DealStatus.REJECTED ||
      bookingStatusCode === DealStatus.TERMINATED ||
      bookingStatusCode === DealStatus.ENDED ||
      bookingStatusCode === DealStatus.FAILED;

    return (
      (areServicesOmitted ? false : areServicesChanged) ||
      !isEqual(
        {
          assets: shouldCompareAvailability ? backupAvailability?.assets : undefined,
          allocatedImpressions: isForecastedAllocation ? backupAvailability?.allocatedImpressions : undefined,
          allocatedFrames: isForecastedAllocation ? backupAvailability?.allocatedFrames : undefined,
          totalCost: isForecastedAllocation ? backupAvailability?.totalCost : undefined,
          startDate: backupStartDate,
          endDate: backupEndDate,
          name: backupName,
          ...(isLineLevelCpm && { cpm: Number(backupCpm) }),
          environments: backupEnvironments,
          impressions: backupImpressions,
          budget: backupBudget,
          frames: backupFrames,
          selectedDays: backupSelectedDays,
          sot: backupSot,
          sweep: backupSweep,
          floorSot: backupFloorSot,
          ceilingSot: backupCeilingSot,
          venueTaxonomies: backupVenueTaxonomies,
          indexOptimisation: formatIndexOptimisationWithEmptyDisplayName(backupIndexOptimisation),
          plannerPoi: backupProximity?.plannerPoi,
          openStreetMapPoi: backupProximity?.openStreetMapPoi,
          selectedSegments: backupSegment?.selectedSegments,
          productFormats: backupProductFormats,
          routeFrameCodes: backupRouteFrameCodes,
          listFiles: backupListFiles,
          countries: backupCountries,
          streets: backupStreets,
          cities: backupCities,
          counties: backupCounties,
          postCodes: backupPostCodes,
          tags: backupTags,
          distributionOfAvailabilityFilter: backupDistributionOfAvailabilityFilter,
          visualUnitCodes: backupVisualUnitCodes,
          visualUnitFiles: backupVisualUnitFiles,
          mediaOwnerMeta: backupMediaOwnerMeta,
          minCPM: backupMinCPM,
          maxCPM: backupMaxCPM,
          frameCpmFiles: backupFrameCpmFiles,
          multiTargetingFile: backupMultiTargetingFile,
        },
        {
          assets: shouldCompareAvailability ? availability.assets : undefined,
          allocatedImpressions: isForecastedAllocation ? availability.allocatedImpressions : undefined,
          allocatedFrames: isForecastedAllocation ? availability.allocatedFrames : undefined,
          totalCost: isForecastedAllocation ? availability.totalCost : undefined,
          startDate,
          endDate,
          name,
          ...(isLineLevelCpm && { cpm: Number(cpm) }),
          environments,
          impressions,
          budget,
          frames,
          selectedDays,
          sot,
          sweep,
          floorSot,
          ceilingSot,
          venueTaxonomies,
          indexOptimisation: formatIndexOptimisationWithEmptyDisplayName(indexOptimisation),
          plannerPoi,
          openStreetMapPoi,
          selectedSegments,
          productFormats,
          routeFrameCodes,
          listFiles,
          countries,
          streets,
          cities,
          counties,
          postCodes,
          tags,
          distributionOfAvailabilityFilter,
          visualUnitCodes,
          visualUnitFiles,
          mediaOwnerMeta,
          minCPM,
          maxCPM,
          frameCpmFiles,
          multiTargetingFile,
        },
      )
    );
  },
);

export const getIsProgrammaticDealLevelChange = createSelector(
  [
    (state: Store) => state.dealManagement.backupFormData.commonDeal,
    (state: Store) => state.dealManagement.backupFormData.programmatic,
    (state: Store) => state.dealManagement.backupFormData.lines,
    (state: Store) => state.dealManagement.commonDeal,
    (state: Store) => state.dealManagement.programmatic,
    (state: Store) => state.dealManagement.programmatic.dsp,
    getIsNewDeal,
    (state: Store) => state.dealManagement.isCpmCampaignLevel,
  ],
  (backupDeal, backupProgrammatic, backupLines, commonDeal, programmatic, _dsp, isNewDeal, isCpmCampaignLevel) => {
    if (isNewDeal && !commonDeal.bookingStatusCode) {
      return false;
    }

    const backupIsCpmCampaignLevel =
      backupLines.length <= 1 || backupLines.every((line) => Number(line.cpm) === Number(backupLines[0].cpm));

    if (isCpmCampaignLevel !== backupIsCpmCampaignLevel) {
      return true;
    }

    const {
      cpm: backupCpm,
      dealName: backupDealName,
      dealType: backupDealType,
      advertiser: backupAdvertiser,
      brand: backupBrand,
      productCategory: backupProductCategory,
      agency: backupAgency,
      specialist: backupSpecialist,
      dealPriority: backupDealPriority,
      salesPerson: backupSalesPerson,
      salesTeam: backupSalesTeam,
    } = backupDeal;

    const {
      cpm,
      dealName,
      dealPriority,
      dealType,
      advertiser,
      brand,
      agency,
      specialist,
      productCategory,
      salesPerson,
      salesTeam,
    } = commonDeal;

    const {
      dsp: backupDsp,
      dspSeatId: backupDspSeatId,
      syncWithDsp: backupSyncWithDsp,
      enableCreativeSubmissionInBidStream: backupEnableCreativeSubmissionInBidStream,
      enableOpenMarketplace: backupEnableOpenMarketplace,
      enableLineLevelTrading: backupEnableLineLevelTrading,
    } = backupProgrammatic;
    const {
      dsp,
      dspSeatId,
      syncWithDsp,
      enableCreativeSubmissionInBidStream,
      enableOpenMarketplace,
      enableLineLevelTrading,
    } = programmatic;

    return !isEqual(
      {
        ...(backupIsCpmCampaignLevel && { cpm: Number(backupCpm) }),
        isCpmCampaignLevel: backupIsCpmCampaignLevel,
        dealName: backupDealName,
        dsp: backupDsp,
        dspSeatId: backupDspSeatId,
        syncWithDsp: backupSyncWithDsp,
        enableCreativeSubmissionInBidStream: backupEnableCreativeSubmissionInBidStream,
        enableOpenMarketplace: backupEnableOpenMarketplace,
        enableLineLevelTrading: backupEnableLineLevelTrading,
        dealPriority: backupDealPriority,
        dealType: backupDealType,
        advertiser: backupAdvertiser,
        brand: backupBrand,
        agency: backupAgency,
        specialist: backupSpecialist,
        productCategory: backupProductCategory,
        salesPerson: backupSalesPerson,
        salesTeam: backupSalesTeam,
      },
      {
        ...(isCpmCampaignLevel && { cpm: Number(cpm) }),
        isCpmCampaignLevel,
        dealName,
        dsp,
        dspSeatId,
        syncWithDsp,
        enableCreativeSubmissionInBidStream,
        enableOpenMarketplace,
        enableLineLevelTrading,
        dealPriority,
        dealType,
        advertiser,
        brand,
        agency,
        specialist,
        productCategory,
        salesPerson,
        salesTeam,
      },
    );
  },
);

export const getDirectSalesSaveDisabled = createSelector(
  (state: Store) => state.dealManagement.commonDeal.advertiser,
  (state: Store) => state.dealManagement.commonDeal.brand,
  (state: Store) => state.dealManagement.commonDeal.productCategory,
  (state: Store) => state.dealManagement.commonDeal.salesPerson,
  (state: Store) => state.dealManagement.commonDeal.salesTeam,
  (state: Store) => state.dealManagement.commonDeal.adminPerson,
  (state: Store) => state.publisher.directSalesRequiredFields,
  (advertiser, brand, productCategory, salesPerson, salesTeam, adminPerson, fieldsConfig) =>
    !Object.values(
      getDirectSalesMandatoryCampaignFields({
        fieldsConfig,
        advertiser,
        brand,
        productCategory,
        salesPerson,
        salesTeam,
        adminPerson,
      }),
    ).every(Boolean),
);

export const getIsDV360 = createSelector([(state: Store) => state.dealManagement.programmatic?.dsp], (dsp) =>
  /dv360/i.test(dsp?.name ?? ''),
);

export const getIsDV360AndPGAndDspSyncEnabled = createSelector(
  [
    getDealType,
    getIsDV360,
    (state: Store) => state.dealManagement.programmatic?.syncWithDsp,
    (state: Store) => state.dealManagement.campaignType,
  ],
  (dealType, isDV360, isWithDspSync, campaignType) => {
    return dealType === DealType.GUARANTEED && isDV360 && isWithDspSync && campaignType === CampaignType.PROGRAMMATIC;
  },
);

export const getActionConditions = createSelector(
  [
    (state: Store) => state.dealManagement,
    (state: Store) => state.dealConfig,
    getIsReseller,
    getIsAnyLineFetchingAvailability,
    getIsDV360AndPGAndDspSyncEnabled,
    (state: Store) => state.reseller.isInternalPublisher,
    getIsProgrammaticDraftCampaignTransitionEnabled,
    getIsProgrammaticEndedAddNewLineEnabled,
    getHasDealReadOnlyPermission,
  ],
  (
    formState,
    dealConfig,
    isReseller,
    isFetchingAvailability,
    isDV360AndPGAndDspSyncEnabled,
    hasVIOOHSelectFeatures,
    isProgrammaticDraftCampaignTransitionEnabled,
    isProgrammaticEndedCampaignTransitionEnabled,
    hasDealReadOnlyPermission,
  ) => {
    const actionConditions = getActionConditionsValidator({
      formState,
      dealConfig,
      isReseller,
      isFetchingAvailability,
      isDV360AndPGAndDspSyncEnabled,
      hasVIOOHSelectFeatures,
      isProgrammaticDraftCampaignTransitionEnabled,
      isProgrammaticEndedCampaignTransitionEnabled,
    });

    if (!hasDealReadOnlyPermission) return actionConditions;

    return Object.fromEntries(
      Object.entries(actionConditions).map(([action, condition]) => [
        action,
        action === 'editDealInfo' ? condition : { ...condition, disabled: true },
      ]),
    );
  },
);

const getProgrammaticSalesDisabledBase = createSelector(
  [
    (state: Store) => state.dealManagement,
    getIsProgrammaticDealLevelChange,
    getIsProgrammaticLineLevelChange,
    getActionConditions,
    (state: Store) => state.dealManagement.hasUnsavedProposal,
  ],
  (
    dealManagementState,
    isDealLevelChange,
    isLineLevelChange,
    { saveDealInfo, editDraftDeal, pendingApprovalDeal, confirmDeal },
    hasUnsavedProposal,
  ) => {
    const {
      isNewDeal,
      isEditingDisabled,
      isLoading,
      commonDeal: { bookingStatusCode },
    } = dealManagementState;

    const isProposePermissionMissing = !Auth.hasPermission(PermissionsEnum.DEAL_PROPOSE);
    const isEditPermissionMissing = !Auth.hasPermission(PermissionsEnum.DEAL_SAVE);
    const isConfirmPermissionMissing = !Auth.hasPermission(PermissionsEnum.DEAL_CONFIRM);
    const isNoDataChanged = hasUnsavedProposal ? false : !(isDealLevelChange || isLineLevelChange);
    const isDraft = bookingStatusCode === DealStatus.DRAFT;

    return {
      isEditingDisabled,
      isLoading,
      isNewDeal,
      isNoDataChanged,
      isDraft,
      isProposePermissionMissing,
      isEditPermissionMissing,
      isConfirmPermissionMissing,
      bookingStatusCode,
      saveDealInfo,
      editDraftDeal,
      pendingApprovalDeal,
      confirmDeal,
    };
  },
);

export const getProgrammaticSalesSaveDisabled = createSelector(
  [getProgrammaticSalesDisabledBase],
  ({
    isEditingDisabled,
    isLoading,
    isNewDeal,
    isNoDataChanged,
    isDraft,
    isProposePermissionMissing,
    isEditPermissionMissing,
    isConfirmPermissionMissing,
    bookingStatusCode,
    saveDealInfo,
    editDraftDeal,
  }) =>
    isEditingDisabled ||
    isLoading ||
    isNoDataChanged ||
    isProposePermissionMissing ||
    (isEditPermissionMissing && !isNewDeal && !isDraft) ||
    (isConfirmPermissionMissing &&
      [DealStatus.APPROVED, DealStatus.REJECTED, DealStatus.LIVE].includes(bookingStatusCode)) ||
    (isDraft ? editDraftDeal.disabled : saveDealInfo.disabled),
);

export const getProgrammaticSaveDealDisabled = createSelector(
  [getProgrammaticSalesDisabledBase, getProgrammaticSalesSaveDisabled],
  ({ bookingStatusCode, pendingApprovalDeal, confirmDeal }, isSaveDisabled) =>
    isSaveDisabled ||
    (bookingStatusCode === DealStatus.PENDING_APPROVAL && pendingApprovalDeal.disabled) ||
    ([DealStatus.APPROVED, DealStatus.LIVE].includes(bookingStatusCode) && confirmDeal.disabled),
);

export const getLoading = createSelector(
  (state: Store) => state.dealManagement.loading,
  (loading) => (loadingLabel: Loading | LocalLoading) => loading[loadingLabel] ?? false,
);

/* eslint-enable sonarjs/cognitive-complexity */
